import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";

/**
 * The 2D-2 comparison verbs.
 *
 * Declared here rather than in `common` for the same reason the script asset types are: nothing
 * outside this feature consumes them yet, and a shared type forces a rebuild gate for no benefit.
 */

export type OutputVerdict = {
  port: string;
  comparator: "exact" | "text" | "exists";
  status: "pass" | "fail";
  message: string;
  /** Set when a text comparison fell back to a checksum because the output exceeded the inline
   *  cap. Normalisation does not apply on that path, and the UI must say so. */
  degraded?: { reason: "over-inline-cap"; bytes: number; cap: number };
  /** Unified diff, present only on a failing text comparison within the cap. */
  diff?: string;
};

/**
 * How much of the contract the expectations document actually asserts on.
 *
 * A second axis, deliberately kept apart from the pass/fail verdict: every entry in a document
 * can match while a declared output has no entry at all, and the publication ladder refuses that
 * run for exactly this reason.
 */
export type PortCoverage = {
  status: "complete" | "partial" | "empty";
  coveredPorts: string[];
  waivedPorts: { port: string; reason: string }[];
  /** Required ports that are neither asserted on nor waived. */
  uncoveredPorts: string[];
};

export type ComparisonReport = {
  status: "pass" | "fail";
  outputs: OutputVerdict[];
  /** Optional for the same reason the capture report's fields are: a tab served ahead of its
   *  server still renders, it just has no coverage verdict to show. */
  coverage?: PortCoverage;
};

export type PromoteResult = {
  path: string;
  contents: string;
  /** Per-port capture report. Optional so a tab served ahead of its server still renders. */
  captured?: { port: string; file: string; note?: string }[];
  skipped?: { port: string; reason: string }[];
  /** Entries and waivers kept for ports the contract no longer declares. */
  retained?: { port: string; reason: string }[];
  /** Declared ports the author waived. Never captured, never skipped. */
  waived?: { port: string; reason: string }[];
};

/** What a promote WOULD do, plus the run's published files, for the match dialog. */
export type CapturePlan = {
  captured: { port: string; file: string; note?: string }[];
  skipped: { port: string; reason: string }[];
  retained?: { port: string; reason: string }[];
  waived?: { port: string; reason: string }[];
  produced: { path: string; bytes: number }[];
};

/** One explicit port to file choice from the dialog. `file` is a produced file's exact pubweb
 *  relative path, never a glob. */
export type PortFileMapping = { port: string; file: string };

/**
 * The server's own words for a refusal, not axios's.
 *
 * ⚠️ `error.message` on a rejected request is always "Request failed with status code 400". The
 * text worth reading is in the response body, which `expressErrorHandlerMiddleware` sends as
 * `{ message }` and which for these two verbs carries the whole diagnosis: which port matched
 * nothing, or that the run published no files at all. Rendering `error.message` instead looks
 * like a working error path while telling the reader nothing they can act on.
 *
 * Same shape as `detailOf` in `script-assets.api.tsx`; kept local because the fallbacks differ
 * per verb and a shared one would have to be generic enough to be useless.
 */
export const serverMessage = (
  error: ErrorNotification | null,
  fallback: string
): string => error?.response?.data?.message ?? fallback;

export const useCompareRunAgainstExpected = (
  processId: number | null | undefined,
  runId: string | number | null | undefined
): UseMutationResult<ComparisonReport, ErrorNotification, void> =>
  useMutation<ComparisonReport, ErrorNotification, void>({
    mutationFn: async () => {
      const res = await axios.post(
        `/api/v1/process/${processId}/runs/${runId}/compare`
      );
      return res.data as ComparisonReport;
    },
  });

export const usePromoteRunToExpected = (
  processId: number | null | undefined,
  runId: string | number | null | undefined
): UseMutationResult<
  PromoteResult,
  ErrorNotification,
  { mapping?: PortFileMapping[] } | void
> =>
  useMutation<
    PromoteResult,
    ErrorNotification,
    { mapping?: PortFileMapping[] } | void
  >({
    mutationFn: async variables => {
      const res = await axios.post(
        `/api/v1/process/${processId}/runs/${runId}/promote`,
        variables && variables.mapping?.length
          ? { mapping: variables.mapping }
          : undefined
      );
      return res.data as PromoteResult;
    },
  });

/**
 * The read half of the match dialog. A mutation rather than a query on purpose: it is fetched
 * on the Capture click, against the run's files as they are at that moment, and a cached
 * answer would offer the user stale files to choose from.
 *
 * ⛔ POST, even though it reads. The endpoint lists and stats every file under the run's pubweb
 * tree, and the same rule that keeps compare and promote off GET keeps this off it too: a link
 * preview or prefetch must never trigger that walk, and the answer must never be cached.
 */
export const useCapturePlan = (
  processId: number | null | undefined,
  runId: string | number | null | undefined
): UseMutationResult<CapturePlan, ErrorNotification, void> =>
  useMutation<CapturePlan, ErrorNotification, void>({
    mutationFn: async () => {
      const res = await axios.post(
        `/api/v1/process/${processId}/runs/${runId}/capture-plan`
      );
      return res.data as CapturePlan;
    },
  });
